
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { 
  CommandDialog, 
  CommandInput, 
  CommandList, 
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut 
} from "@/components/ui/command";
import { useHotkeys } from "@/hooks/useHotkeys";

type SearchableRoute = {
  title: string;
  url: string;
  keywords?: string[];
  shortcut?: string;
  icon?: React.ReactNode;
}

export function CommandPalette() {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();

  // Available routes for quick navigation
  const routes: SearchableRoute[] = [
    { title: "Dashboard", url: "/dashboard", keywords: ["home", "main"], shortcut: "D" },
    { title: "Analytics", url: "/analytics", keywords: ["stats", "metrics", "charts"], shortcut: "A" },
    { title: "Teams", url: "/teams", keywords: ["people", "groups", "members"], shortcut: "T" },
    { title: "Notifications", url: "/notifications", keywords: ["alerts", "messages"], shortcut: "N" },
    { title: "HR Chatbot", url: "/hr-chatbot", keywords: ["assistant", "help", "support"], shortcut: "H" },
    { title: "Team Matching", url: "/team-matching", keywords: ["pairing", "matching", "allocation"], shortcut: "M" },
    { title: "Profile", url: "/profile", keywords: ["account", "settings", "user"], shortcut: "P" },
    { title: "Integrations", url: "/integrations", keywords: ["connect", "api", "services"], shortcut: "I" },
    { title: "Billing", url: "/billing", keywords: ["payment", "subscription", "plans"], shortcut: "B" },
  ];

  // Handle keyboard shortcut
  useHotkeys("mod+k", () => {
    setOpen(true);
  });
  
  // Navigate and close the command palette
  const runCommand = React.useCallback((command: () => void) => {
    command();
    setOpen(false);
  }, []);

  return (
    <>
      <button 
        onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center gap-2 text-sm h-9 rounded-md border border-input bg-background px-3 text-muted-foreground shadow-sm hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <Search className="h-4 w-4" />
        <span className="hidden md:inline">Search...</span>
        <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100 sm:flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </button>
      
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search for pages, features, or help..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup heading="Navigation">
            {routes.map((route) => (
              <CommandItem
                key={route.url}
                value={`${route.title} ${route.keywords?.join(' ')}`}
                onSelect={() => runCommand(() => navigate(route.url))}
              >
                {route.title}
                {route.shortcut && (
                  <CommandShortcut>⌘{route.shortcut}</CommandShortcut>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
